import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";

const initialState = {
  items: [],
  isLoading: false,
  error: null,
  unreadCount: 0,
  meta: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.isLoading = action.payload;
    },
    setNotifications(state, action) {
      state.items = action.payload.items || [];
      state.meta = action.payload.meta || null;
    },
    setUnreadCount(state, action) {
      state.unreadCount = action.payload;
      state.lastFetchedUnreadAt = Date.now();
    },
    setError(state, action) {
      state.error = action.payload;
    },
    markAsRead(state, action) {
      const notificationId = action.payload;
      const item = state.items.find((n) => n._id === notificationId);
      if (item && item.status === "unread") {
        item.status = "read";
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllRead(state) {
      state.items.forEach((n) => { n.status = "read"; });
      state.unreadCount = 0;
    },
    clearNotifications(state) {
      state.items = [];
      state.unreadCount = 0;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setNotifications,
  setUnreadCount,
  setError,
  markAsRead,
  markAllRead,
  clearNotifications,
} = notificationsSlice.actions;

/**
 * 🚀 API: Fetch Notifications (Persona Aware)
 */
export const fetchNotifications = (userType = null, filters = {}) => async (dispatch) => {
  const isProvider = userType === 'serviceProvider' || 
                     (typeof window !== 'undefined' && window.location.pathname.includes('/serviceProvider'));
  
  // NOTE: The exact backend route needs to be verified. 
  // Assuming standard convention or legacy match for now.
  const baseRoute = isProvider ? '/serviceProvider/notifications' : '/client/notifications';
  
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const { page = 1, limit = 20, category } = filters;
    let queryString = `page=${page}&limit=${limit}`;
    if (category) queryString += `&category=${category}`;

    const url = `${baseRoute}?${queryString}`;
    const response = await axiosInstance.get(url);
    const { status, data, meta } = response?.data || {};

    if (status === "success" || response.status === 200) {
      dispatch(setNotifications({ items: data, meta }));
    }
  } catch (error) {
    
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * 🚀 API: Fetch Unread Count
 */
export const fetchUnreadCount = (userType = null) => async (dispatch, getState) => {
  const isProvider = userType === 'serviceProvider' || 
                     (typeof window !== 'undefined' && window.location.pathname.includes('/serviceProvider'));
  const baseRoute = isProvider ? '/serviceProvider/notifications' : '/client/notifications';

  // Throttling: Don't fetch if fetched in the last 30 seconds
  const lastFetched = getState().notifications?.lastFetchedUnreadAt;
  if (lastFetched && Date.now() - lastFetched < 30000) return;

  try {
    const response = await axiosInstance.get(`${baseRoute}/unread/count`);
    const { status, data } = response?.data || {};
    const count = typeof data === 'number' ? data : (data?.unreadCount || 0);
    
    if (status === "success" || response.status === 200) {
      dispatch(setUnreadCount(count));
    }
  } catch (error) {
    console.error("fetchUnreadCount error:", error);
  }
};

/**
 * 🚀 API: Mark Notification as Read
 */
export const markNotificationRead = (notificationId, userType = null) => async (dispatch) => {
  if (!notificationId) return;
  const isProvider = userType === 'serviceProvider' || (typeof window !== 'undefined' && window.location.pathname.includes('/serviceProvider'));
  const baseRoute = isProvider ? '/serviceProvider/notifications' : '/client/notifications';

  try {
    const response = await axiosInstance.patch(`${baseRoute}/${notificationId}/read`);
    if (response?.data?.status === "success") {
      dispatch(markAsRead(notificationId));
    }
  } catch (error) {
    
  }
};

/**
 * 🚀 API: Mark All as Read
 */
export const markAllNotificationsRead = (userType = null) => async (dispatch) => {
  const isProvider = userType === 'serviceProvider' || (typeof window !== 'undefined' && window.location.pathname.includes('/serviceProvider'));
  const baseRoute = isProvider ? '/serviceProvider/notifications' : '/client/notifications';

  try {
    const response = await axiosInstance.patch(`${baseRoute}/read-all`);
    if (response?.data?.status === "success") {
      dispatch(markAllRead());
    }
  } catch (error) {
    
  }
};

export default notificationsSlice.reducer;
